import { useState } from "react";
import { Search } from "lucide-react";

import { useChatSearch } from "../hooks/use-chat-search";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";

export function SearchMessages({ onSelectMessage }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const { data: searchResults, isLoading } = useChatSearch(searchQuery, isSearching && searchQuery.length >= 2);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setIsSearching(true);
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value.length < 2) {
      setIsSearching(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const highlightMatch = (text, query) => {
    if (!query || query.length < 2) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark
          key={index}
          style={{
            backgroundColor: "#fef08a",
            padding: "0 2px",
            borderRadius: "2px",
          }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Search Input */}
      <form
        onSubmit={handleSearch}
        style={{
          padding: "12px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            backgroundColor: "#f3f4f6",
            borderRadius: "6px",
          }}
        >
          <Search style={{ height: "16px", width: "16px", color: "#9ca3af" }} />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={handleInputChange}
            style={{
              flex: 1,
              border: "none",
              backgroundColor: "transparent",
              outline: "none",
              fontSize: "0.875rem",
            }}
            autoFocus
          />
        </div>
        {searchQuery.length > 0 && searchQuery.length < 2 && (
          <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "4px", paddingLeft: "4px" }}>
            Enter at least 2 characters to search
          </p>
        )}
      </form>

      {/* Search Results */}
      <ScrollArea style={{ flex: 1 }}>
        <ScrollBar orientation="vertical" />
        <div style={{ padding: "8px" }}>
          {isLoading && (
            <div style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
              Searching...
            </div>
          )}

          {!isLoading && isSearching && searchResults?.length === 0 && (
            <div style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
              No messages found for "{searchQuery}"
            </div>
          )}

          {!isLoading && !isSearching && (
            <div style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
              Search for messages across all conversations
            </div>
          )}

          {searchResults?.map((result) => (
            <button
              key={result.id}
              onClick={() => onSelectMessage(result.staffId || result.chatPartnerId)}
              style={{
                display: "flex",
                width: "100%",
                gap: "10px",
                padding: "10px",
                border: "none",
                borderRadius: "6px",
                backgroundColor: "transparent",
                cursor: "pointer",
                textAlign: "left",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <Avatar style={{ height: "2.5rem", width: "2.5rem", flexShrink: 0 }}>
                <AvatarImage src={result.senderAvatar} alt={result.senderName} />
                <AvatarFallback>
                  {result.senderName?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "??"}
                </AvatarFallback>
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                  <span style={{ fontWeight: 500, fontSize: "0.875rem", color: "#111827" }}>
                    {result.senderName || "Unknown"}
                  </span>
                  <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                    {result.createdAt && formatDate(result.createdAt)}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "0.8125rem",
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    margin: 0,
                  }}
                >
                  {highlightMatch(result.message, searchQuery)}
                </p>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
